import type { CSSProperties } from "react"
import {
  BookOpen, Brain, CalendarDays, CheckSquare, Code2, Download, ExternalLink,
  FileText, Film, GitBranch, Gamepad2, LayoutGrid, Lock,
  Mail, MonitorPlay, Network, Search, ShieldCheck, ShieldOff, Sword, Swords,
  Tv, Users, Package, Container, Play, Square, RotateCcw,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { rgbFor } from "@/shared/colors"
import type { Extension, InstallMode } from "./types"
import { streamAction } from "./api"

const ICON_MAP: Record<string, React.ElementType> = {
  BookOpen, Brain, CalendarDays, CheckSquare, Code2, Download,
  FileText, Film, GitBranch, Gamepad2, LayoutGrid, Lock,
  Mail, MonitorPlay, Network, Search, ShieldCheck, ShieldOff, Sword, Swords,
  Tv, Users,
}

function ExtIcon({ name }: { name: string }) {
  const Icon = ICON_MAP[name] ?? Package
  return <Icon size={20} />
}

function StatusBadge({ ext }: { ext: Extension }) {
  const { t } = useTranslation("extensions")
  if (!ext.installed) return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-zinc-800 text-zinc-400 border border-white/[6%]">
      {t("status.not_installed")}
    </span>
  )
  if (ext.active && ext.healthy) return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
      {t("status.active")}
    </span>
  )
  if (ext.active) return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
      {t("status.running_unreachable")}
    </span>
  )
  return (
    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-rose-500/15 text-rose-400 border border-rose-500/30">
      {t("status.stopped")}
    </span>
  )
}

interface Props {
  ext: Extension
  onInstall: (mode: InstallMode) => void
  onUninstall: (mode: InstallMode) => void
}

export function ExtensionCard({ ext, onInstall, onUninstall, onRefresh }: Props & { onRefresh?: () => void }) {
  const { t } = useTranslation("extensions")
  const hasDocker = !!ext.docker && ext.docker_available
  const [mode, setMode] = useState<InstallMode>(ext.preferred_mode ?? "native")
  const [dockerBusy, setDockerBusy] = useState<string | null>(null)

  function dockerAction(action: "docker/start" | "docker/stop" | "docker/restart") {
    setDockerBusy(action)
    streamAction(ext.id, action, {}, () => {}, () => { setDockerBusy(null); onRefresh?.() }, () => setDockerBusy(null))
  }

  const openUrl = (() => {
    const raw = (ext.install_mode === "docker" && ext.docker?.open_url)
      ? ext.docker.open_url
      : (ext.open_url ?? "")
    if (!raw) return null
    if (raw.startsWith("http")) return raw
    return `http://${window.location.hostname}${raw}`
  })()

  return (
    <div className="box overflow-hidden flex flex-col gap-3 p-4 transition-opacity hover:opacity-90" style={{ "--c": rgbFor("/mcp") } as CSSProperties}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-violet-500/10 text-violet-400 shrink-0">
          <ExtIcon name={ext.icon} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-zinc-100 text-sm">{ext.name}</span>
            <StatusBadge ext={ext} />
            {ext.install_mode === "docker" && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] bg-blue-500/10 border border-blue-500/20 text-blue-400">
                <Container size={9} /> Docker
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{ext.description}</p>
        </div>
      </div>

      {/* Mode-Toggle — nur sichtbar wenn Docker verfügbar + Extension hat docker-Block + noch nicht installiert */}
      {hasDocker && !ext.installed && (
        <div className="flex rounded-lg overflow-hidden border border-white/[8%] text-xs">
          {(["native", "docker"] as InstallMode[]).map((m) => (
            <button key={m} onClick={() => setMode(m)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 transition-colors ${
                mode === m
                  ? "bg-violet-600 text-white"
                  : "text-zinc-400 hover:text-zinc-200 hover:bg-white/[5%]"
              }`}>
              {m === "docker" ? <><Container size={11} /> {t("mode.docker")}</> : <><Package size={11} /> {t("mode.native")}</>}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {!ext.installed ? (
          <button onClick={() => onInstall(mode)}
            className="flex-1 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-xs font-medium transition-colors">
            {t("actions.install")}
          </button>
        ) : (
          <>
            {openUrl && (
              <a href={openUrl} target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[5%] border border-white/[8%] text-zinc-300 text-xs hover:bg-white/[8%] transition-colors">
                <ExternalLink size={11} /> {t("actions.open")}
              </a>
            )}
            {ext.install_mode === "docker" && (
              <>
                {!ext.active ? (
                  <button onClick={() => dockerAction("docker/start")}
                    disabled={!!dockerBusy}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-400 text-xs font-medium disabled:opacity-40 transition-colors">
                    <Play size={10} /> {dockerBusy === "docker/start" ? "…" : t("actions.start")}
                  </button>
                ) : (
                  <button onClick={() => dockerAction("docker/stop")}
                    disabled={!!dockerBusy}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 text-amber-400 text-xs font-medium disabled:opacity-40 transition-colors">
                    <Square size={10} /> {dockerBusy === "docker/stop" ? "…" : t("actions.stop")}
                  </button>
                )}
                <button onClick={() => dockerAction("docker/restart")}
                  disabled={!!dockerBusy}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 text-blue-400 text-xs font-medium disabled:opacity-40 transition-colors">
                  <RotateCcw size={10} /> {dockerBusy === "docker/restart" ? "…" : t("actions.restart")}
                </button>
              </>
            )}
            <button onClick={() => onUninstall(ext.install_mode ?? "native")}
              className="flex-1 py-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-400 text-xs font-medium transition-colors">
              {t("actions.uninstall")}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
